"use client";

import { FormEvent, useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import styles from "./Search.module.css";

const MIN_QUERY_LENGTH = 3;

export default function Search() {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();
  const urlQuery = searchParams.get("query") ?? "";
  const [value, setValue] = useState(urlQuery);
  const lastPushed = useRef(urlQuery);

  useEffect(() => {
    if (urlQuery !== lastPushed.current) {
      setValue(urlQuery);
      lastPushed.current = urlQuery;
    }
  }, [urlQuery]);

  const applyQuery = (term: string) => {
    const params = new URLSearchParams(searchParams);
    const nextQuery = term.trim().length >= MIN_QUERY_LENGTH ? term.trim() : "";

    if (nextQuery) {
      params.set("query", nextQuery);
    } else {
      params.delete("query");
    }

    params.delete("page");
    params.delete("perPage");

    lastPushed.current = nextQuery;
    const queryString = params.toString();
    replace(queryString ? `${pathname}?${queryString}` : pathname);
  };

  const handleSearch = useDebouncedCallback(applyQuery, 400);

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    handleSearch.cancel();
    applyQuery(value);
  }

  function onClear() {
    setValue("");
    handleSearch.cancel();
    applyQuery("");
  }

  return (
    <form className={styles.wrap} role="search" onSubmit={onSubmit}>
      <div className={styles.form}>
        <input
          className={styles.field}
          type="search"
          name="query"
          value={value}
          onChange={(event) => {
            const next = event.target.value;
            setValue(next);
            handleSearch(next);
          }}
          placeholder="Search posts"
          aria-label="Search posts"
          autoComplete="off"
          enterKeyHint="search"
        />
        {value ? (
          <button
            type="button"
            className={styles.clear}
            onClick={onClear}
            aria-label="Clear search"
          >
            ×
          </button>
        ) : null}
      </div>
    </form>
  );
}
